// StaffApprovedDetail.tsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import StaffLayout from "../components/StaffLayout";

export {}; // module safety

type MerchantApprovedDetail = {
  ok: boolean;
  merchant: {
    merchant_id: string;
    company_name: string;
    industry?: string;
    merchant_type?: string; // 法人 / 個人
    updated_at: string;
  };
  progress: {
    merchant_id: string;
    docs_total: number;
    docs_completed: number;
    uploads_total: number;
    uploads_completed: number;
    status: string;
    forms: Record<string, boolean>;
  };
};

type StaffDocumentItem = {
  document_key: string;
  document_label: string;
  other_title: string;
  file_name: string;
  file_path: string;
  uploaded_at: string;
};

type RingiData = {
  sales_staff_name?: string;
  cuppy_contract?: "" | "有" | "無";
  application_background?: string;
  current_status?: string;

  latest_fiscal_month?: string;
  annual_revenue_million?: string;
  annual_profit_thousand?: string;

  reason?: string;
  planned_credit_company?: string;
  customer_fee_percent?: string;
  upper_amount_man?: string;
  upper_payments?: string;
  merchant_fee_percent?: string;
  sales_opinion?: string;

  ops_staff_name?: string;
  ops_opinion?: string;
  application_date?: string;


  // optional fields if your backend returns them
  is_completed?: boolean;
  completed_at?: string;
  updated_at?: string;

};

const BASE_URL = "/api";

function CircleProgress({ label, value, total }: { label: string; value: number; total: number }) {
  const pct = total > 0 ? Math.round((value / total) * 100) : 0;
  const r = 42;
  const c = 2 * Math.PI * r;
  const offset = c - (pct / 100) * c;

  return (
    <div
      style={{
        border: "1px solid #e2e8f0",
        borderRadius: 14,
        padding: 14,
        background: "#f8fafc",
        display: "flex",
        gap: 14,
        alignItems: "center",
      }}
    >
      <svg width="110" height="110" viewBox="0 0 110 110">
        <circle cx="55" cy="55" r={r} strokeWidth="10" stroke="#e2e8f0" fill="none" />
        <circle
          cx="55"
          cy="55"
          r={r}
          strokeWidth="10"
          stroke="#16a34a"
          fill="none"
          strokeDasharray={c}
          strokeDashoffset={offset}
          strokeLinecap="round"
          transform="rotate(-90 55 55)"
        />
        <text x="55" y="58" textAnchor="middle" fontSize="18" fontWeight="900" fill="#16a34a">
          {pct}%
        </text>
      </svg>

      <div style={{ minWidth: 0 }}>
        <div style={{ fontWeight: 900, color: "#0f172a" }}>{label}</div>
        <div style={{ marginTop: 6, fontWeight: 900, fontSize: 18, color: "#16a34a" }}>
          {value} / {total}
        </div>
        <div style={{ marginTop: 6, color: "#64748b", fontWeight: 700, fontSize: 12 }}>
          進捗率（自動計算）
        </div>
      </div>
    </div>
  );
}

function FieldRow({ label, value }: { label: string; value?: string }) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "240px 1fr",
        gap: 12,
        padding: "10px 12px",
        borderTop: "1px solid #e2e8f0",
        alignItems: "start",
      }}
    >
      <div style={{ fontWeight: 900, color: "#0f172a", fontSize: 13 }}>{label}</div>
      <div style={{ fontWeight: 800, color: "#334155", fontSize: 13, whiteSpace: "pre-wrap" }}>
        {value && String(value).trim() ? value : "-"}
      </div>
    </div>
  );
}




function ExcelRingiTable({ ringi }: { ringi: RingiData | null }) {
  const v = (x?: string) => (x && String(x).trim() ? String(x) : "-");

  const cellStyle: React.CSSProperties = {
    border: "1px solid #111827",
    padding: "8px 10px",
    fontSize: 13,
    fontWeight: 700,
    color: "#0f172a",
    verticalAlign: "top",
    background: "#fff",
    whiteSpace: "pre-wrap",
  };

  const labelStyle: React.CSSProperties = {
    ...cellStyle,
    width: 180,
    background: "#f8fafc",
    fontWeight: 900,
  };

  const bigBox: React.CSSProperties = {
    ...cellStyle,
    height: 120,
  };

  
  return (
    <div style={{ overflowX: "auto" }}>
      <table style={{ borderCollapse: "collapse", width: "100%", minWidth: 900 }}>
        <tbody>
          <tr>
            <td style={{ ...cellStyle, fontWeight: 900 }} colSpan={6}>
  申請日： {v((ringi as any)?.application_date)}
</td>

          </tr>

          <tr>
            <td style={labelStyle}>担当営業</td>
            <td style={cellStyle} colSpan={5}>{v(ringi?.sales_staff_name)}</td>
          </tr>

          <tr>
            <td style={labelStyle}>Cuppy（有/無）</td>
            <td style={cellStyle} colSpan={5}>{v(ringi?.cuppy_contract)}</td>
          </tr>

          <tr>
            <td style={labelStyle}>申請背景</td>
            <td style={bigBox} colSpan={5}>{v(ringi?.application_background)}</td>
          </tr>

          <tr>
            <td style={labelStyle}>現状</td>
            <td style={bigBox} colSpan={5}>{v(ringi?.current_status)}</td>
          </tr>

          <tr>
            <td style={labelStyle}>決算（直近年度）</td>
            <td style={cellStyle} colSpan={2}>{v(ringi?.latest_fiscal_month)}</td>
            <td style={labelStyle}>年商（百万円）</td>
            <td style={cellStyle} colSpan={2}>{v(ringi?.annual_revenue_million)}</td>
          </tr>

          <tr>
            <td style={labelStyle}>当期利益（千円）</td>
            <td style={cellStyle} colSpan={5}>{v(ringi?.annual_profit_thousand)}</td>
          </tr>

          <tr>
            <td style={labelStyle}>理由</td>
            <td style={bigBox} colSpan={5}>{v(ringi?.reason)}</td>
          </tr>

          <tr>
            <td style={labelStyle}>導入予定信販会社</td>
            <td style={cellStyle} colSpan={5}>{v(ringi?.planned_credit_company)}</td>
          </tr>

          <tr>
            <td style={labelStyle}>顧客手数料（%）</td>
            <td style={cellStyle} colSpan={2}>{v(ringi?.customer_fee_percent)}</td>
            <td style={labelStyle}>上限金額（万円）</td>
            <td style={cellStyle} colSpan={2}>{v(ringi?.upper_amount_man)}</td>
          </tr>

          <tr>
            <td style={labelStyle}>上限支払回数（回）</td>
            <td style={cellStyle} colSpan={2}>{v(ringi?.upper_payments)}</td>
            <td style={labelStyle}>加盟店手数料（%）</td>
            <td style={cellStyle} colSpan={2}>{v(ringi?.merchant_fee_percent)}</td>
          </tr>

          <tr>
            <td style={labelStyle}>営業担当所見</td>
            <td style={{ ...bigBox, height: 140 }} colSpan={5}>{v(ringi?.sales_opinion)}</td>
          </tr>

          <tr>
            <td style={labelStyle}>業務担当</td>
            <td style={cellStyle} colSpan={5}>{v(ringi?.ops_staff_name)}</td>
          </tr>

          <tr>
            <td style={labelStyle}>業務担当所見</td>
            <td style={{ ...bigBox, height: 140 }} colSpan={5}>{v(ringi?.ops_opinion)}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

export default function StaffApprovedDetail() {
  const navigate = useNavigate();
  const { merchantId } = useParams();

  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState<MerchantApprovedDetail | null>(null);
  const [error, setError] = useState("");

  // ✅ Section toggle: forms / uploads / approval
  const [section, setSection] = useState<"forms" | "uploads" | "approval">("forms");

  // ✅ Uploaded documents
  const [docsLoading, setDocsLoading] = useState(false);
  const [docsError, setDocsError] = useState("");
  const [docs, setDocs] = useState<StaffDocumentItem[]>([]);

  // ✅ Ringi (approval form)
  const [ringiLoading, setRingiLoading] = useState(false);
  const [ringiError, setRingiError] = useState("");
  const [ringi, setRingi] = useState<RingiData | null>(null);

  const formsList = useMemo(
    () => [
      { key: "form1_company_overview", label: "フォーム① 会社概要" },
      { key: "form2_representative", label: "フォーム② 代表者情報" },
      { key: "form3_payment_handling", label: "フォーム③ 決済取扱" },
      { key: "form4_products", label: "フォーム④ 取扱商品" },
      { key: "form5_sales_method", label: "フォーム⑤ 販売方法" },
      { key: "form6_store_list", label: "フォーム⑥ 店舗一覧" },
      { key: "form7_contact_person", label: "フォーム⑦ ご担当者情報" },
    ],
    []
  );

  // --------------------------
  // Fetch: approved detail (reuse progress detail endpoint)
  // --------------------------
  useEffect(() => {
    const run = async () => {
      if (!merchantId) return;

      setError("");
      setLoading(true);

      try {
        const res = await fetch(`${BASE_URL}/staff/progress/${merchantId}`);
        const json = await res.json();
        if (!res.ok) throw new Error(json?.detail || "detail fetch failed");

        setDetail(json);
        setLoading(false);
      } catch (e: any) {
        setError(e?.message || "承認済み詳細の取得に失敗しました。");
        setLoading(false);
      }
    };

    run();
  }, [merchantId]);

  // --------------------------
  // Fetch: uploaded docs (when uploads tab)
  // --------------------------
  useEffect(() => {
    const runDocs = async () => {
      if (!merchantId) return;
      if (section !== "uploads") return;

      setDocsError("");
      setDocsLoading(true);

      try {
        const res = await fetch(`${BASE_URL}/staff/documents/${merchantId}`);
        const json = await res.json();
        if (!res.ok) throw new Error(json?.detail || "documents fetch failed");

        const items: StaffDocumentItem[] = Array.isArray(json?.items) ? json.items : [];
        setDocs(items);
        setDocsLoading(false);
      } catch (e: any) {
        setDocsError(e?.message || "書類一覧の取得に失敗しました。");
        setDocsLoading(false);
      }
    };

    runDocs();
  }, [merchantId, section]);

  // --------------------------
  // Fetch: ringi (approval form) when approval tab
  // --------------------------
  useEffect(() => {
    const runRingi = async () => {
      if (!merchantId) return;
      if (section !== "approval") return;

      setRingiError("");
      setRingiLoading(true);

      try {
        const res = await fetch(`${BASE_URL}/staff/ringi/${merchantId}`);
        const json = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(json?.detail || "ringi fetch failed");

        setRingi((json?.data || {}) as RingiData);
        setRingiLoading(false);
      } catch (e: any) {
        setRingiError(e?.message || "承認（稟議）情報の取得に失敗しました。");
        setRingiLoading(false);
      }
    };

    runRingi();
  }, [merchantId, section]);

  const docsMap = useMemo(() => {
    const m: Record<string, StaffDocumentItem> = {};
    for (const d of docs) {
      if (d?.document_key) m[d.document_key] = d;
    }
    return m;
  }, [docs]);

  return (
    <StaffLayout title="承認済み 詳細" active="approvedMerchants">
      <button
        onClick={() => navigate("/staff/approved")}
        style={{
          padding: "10px 14px",
          borderRadius: 10,
          border: "1px solid #cbd5e1",
          background: "#fff",
          color: "#0f172a",
          fontWeight: 900,
          cursor: "pointer",
          marginBottom: 12,
        }}
      >
        戻る（承認済み一覧）
      </button>

      {loading && <div style={{ color: "#475569", fontWeight: 800 }}>読み込み中...</div>}

      {error && (
        <div
          style={{
            marginTop: 10,
            background: "#fff1f2",
            border: "1px solid #fecdd3",
            color: "#b91c1c",
            padding: "10px 12px",
            borderRadius: 12,
            fontWeight: 800,
          }}
        >
          {error}
        </div>
      )}

      {!loading && !error && detail && (
        <>
          {/* Merchant info */}
          <div
            style={{
              border: "1px solid #bae6fd",
              background: "#f0f9ff",
              borderRadius: 14,
              padding: 14,
              marginBottom: 14,
            }}
          >
            <div style={{ fontWeight: 900, marginBottom: 6 }}>加盟店情報</div>

            <div style={{ lineHeight: 1.8, fontWeight: 500, color: "#0f172a" }}>
              <div>加盟店ID：{detail.merchant.merchant_id}</div>
              <div>会社名：{detail.merchant.company_name}</div>
              <div>業種：{detail.merchant.industry || "-"}</div>
              <div>区分：{detail.merchant.merchant_type || "-"}</div>
              <div>ステータス：{detail.progress.status}</div>
              <div style={{ color: "#475569", fontWeight: 500, fontSize: 13 }}>
                最終更新：{detail.merchant.updated_at}
              </div>
            </div>
          </div>

          {/* Circles */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <CircleProgress
              label="フォーム入力（7件）"
              value={detail.progress.docs_completed}
              total={detail.progress.docs_total}
            />
            <CircleProgress
              label={`書類アップロード（${detail.progress.uploads_total}件）`}
              value={detail.progress.uploads_completed}
              total={detail.progress.uploads_total}
            />
          </div>

          {/* Tabs */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginTop: 12 }}>
            <div
              onClick={() => setSection("forms")}
              style={{
                padding: 14,
                borderRadius: 14,
                border: section === "forms" ? "2px solid #2563eb" : "1px solid #2563eb",
                background: section === "forms" ? "#eef2ff" : "#fff",
                cursor: "pointer",
                fontWeight: 900,
                color: "#0f172a",
                textAlign: "center",
              }}
            >
              フォーム
            </div>

            <div
              onClick={() => setSection("uploads")}
              style={{
                padding: 14,
                borderRadius: 14,
                border: section === "uploads" ? "2px solid #2563eb" : "1px solid #2563eb",
                background: section === "uploads" ? "#eef2ff" : "#fff",
                cursor: "pointer",
                fontWeight: 900,
                color: "#0f172a",
                textAlign: "center",
              }}
            >
              書類
            </div>

            <div
              onClick={() => setSection("approval")}
              style={{
                padding: 14,
                borderRadius: 14,
                border: section === "approval" ? "2px solid #2563eb" : "1px solid #2563eb",
                background: section === "approval" ? "#eef2ff" : "#fff",
                cursor: "pointer",
                fontWeight: 900,
                color: "#0f172a",
                textAlign: "center",
              }}
            >
              承認（稟議）
            </div>
          </div>

          {/* =========================
              FORMS (no checkbox)
             ========================= */}
          {section === "forms" && (
            <div
              style={{
                marginTop: 14,
                border: "1px solid #e2e8f0",
                borderRadius: 14,
                background: "#fff",
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  padding: 12,
                  fontWeight: 900,
                  background: "#f8fafc",
                  borderBottom: "1px solid #e2e8f0",
                }}
              >
                入力済みフォーム一覧
              </div>

              <div style={{ padding: 12 }}>
                {formsList.map((f) => {
                  const done = !!detail.progress.forms?.[f.key];

                  const handleClick = () => {
                    if (!merchantId) return;
                    if (!done) return;
                    // ✅ reuse existing detail route
                    navigate(`/staff/progress/${merchantId}/forms/${f.key}`);
                  };

                  return (
                    <div
                      key={f.key}
                      onClick={handleClick}
                      role={done ? "button" : undefined}
                      tabIndex={done ? 0 : -1}
                      onKeyDown={(e) => {
                        if (!done) return;
                        if (e.key === "Enter" || e.key === " ") handleClick();
                      }}
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        padding: "12px 12px",
                        borderRadius: 12,
                        border: done ? "1px solid #bbf7d0" : "1px solid #f1f5f9",
                        marginBottom: 10,
                        background: done ? "#ecfdf5" : "#fff7ed",
                        cursor: done ? "pointer" : "not-allowed",
                        opacity: done ? 1 : 0.85,
                        userSelect: "none",
                        alignItems: "center",
                        gap: 10,
                      }}
                      title={done ? "クリックして入力内容を表示" : "未完了のため表示できません"}
                    >
                      <div style={{ fontWeight: 900, color: "#0f172a", minWidth: 0 }}>
                        <div>{f.label}</div>
                        {done && (
                          <div style={{ marginTop: 4, fontSize: 12, fontWeight: 900, color: "#0b1f3b" }}>
                            （詳細を見る）
                          </div>
                        )}
                      </div>

                      <div style={{ fontWeight: 900, color: done ? "#166534" : "#9a3412", minWidth: 60 }}>
                        {done ? "完了" : "未完了"}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* =========================
              UPLOADS (no checkbox)
             ========================= */}
          {section === "uploads" && (
            <div
              style={{
                marginTop: 14,
                border: "1px solid #e2e8f0",
                borderRadius: 14,
                background: "#fff",
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  padding: 12,
                  fontWeight: 900,
                  background: "#f8fafc",
                  borderBottom: "1px solid #e2e8f0",
                }}
              >
                アップロード書類一覧
              </div>

              <div style={{ padding: 12 }}>
                {docsLoading && <div style={{ color: "#475569", fontWeight: 800 }}>読み込み中...</div>}

                {docsError && (
                  <div
                    style={{
                      marginTop: 10,
                      background: "#fff1f2",
                      border: "1px solid #fecdd3",
                      color: "#b91c1c",
                      padding: "10px 12px",
                      borderRadius: 12,
                      fontWeight: 800,
                    }}
                  >
                    {docsError}
                  </div>
                )}

                {!docsLoading && !docsError && docs.length === 0 && (
                  <div style={{ color: "#475569", fontWeight: 800 }}>アップロードされた書類がありません。</div>
                )}

                {!docsLoading &&
                  !docsError &&
                  docs.map((uploaded) => {
                    const title = uploaded?.document_label
                      ? uploaded.other_title
                        ? `${uploaded.document_label}（${uploaded.other_title}）`
                        : uploaded.document_label
                      : uploaded.document_key;

                    const handleClick = () => {
                      if (!merchantId) return;
                      navigate(`/staff/progress/${merchantId}/documents/${uploaded.document_key}`);
                    };

                    return (
                      <div
                        key={uploaded.document_key}
                        style={{
                          borderRadius: 12,
                          border: "1px solid #bbf7d0",
                          marginBottom: 10,
                          background: "#ecfdf5",
                          overflow: "hidden",
                        }}
                      >
                        <div
                          onClick={handleClick}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") handleClick();
                          }}
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            padding: "12px 12px",
                            cursor: "pointer",
                            userSelect: "none",
                            alignItems: "center",
                            gap: 10,
                          }}
                          title="クリックして書類詳細を表示"
                        >
                          <div style={{ fontWeight: 900, color: "#0f172a", minWidth: 0 }}>
                            <div style={{ wordBreak: "break-word" }}>{title}</div>
                            <div style={{ marginTop: 4, fontSize: 12, fontWeight: 900, color: "#0b1f3b" }}>
                              （詳細を見る）
                            </div>
                          </div>

                          <div style={{ fontWeight: 900, color: "#166534", minWidth: 60 }}>完了</div>
                        </div>

                        <div style={{ padding: "0 12px 12px 12px" }}>
                          <div style={{ marginTop: 6, fontSize: 12, fontWeight: 800, color: "#334155" }}>
                            ファイル：
                            <a
                              href={`${BASE_URL}/staff/documents/${merchantId}/${uploaded.document_key}/download`}
                              target="_blank"
                              rel="noreferrer"
                              style={{
                                marginLeft: 6,
                                color: "#2563eb",
                                textDecoration: "underline",
                                fontWeight: 900,
                                wordBreak: "break-all",
                              }}
                              title="クリックして開く／ダウンロード"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {uploaded.file_name}
                            </a>
                          </div>

                          <div style={{ marginTop: 4, fontSize: 12, fontWeight: 700, color: "#64748b" }}>
                            提出日：{uploaded.uploaded_at}
                          </div>
                        </div>
                      </div>
                    );
                  })}
              </div>
            </div>
          )}

          {/* =========================
              APPROVAL / RINGI
             ========================= */}
          {section === "approval" && (
            <div
              style={{
                marginTop: 14,
                border: "1px solid #e2e8f0",
                borderRadius: 14,
                background: "#fff",
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  padding: 12,
                  fontWeight: 900,
                  background: "#f8fafc",
                  borderBottom: "1px solid #e2e8f0",
                }}
              >
                承認フォーム（稟議）
              </div>

              {ringiLoading && <div style={{ padding: 12, color: "#475569", fontWeight: 800 }}>読み込み中...</div>}

              {ringiError && (
                <div
                  style={{
                    margin: 12,
                    background: "#fff1f2",
                    border: "1px solid #fecdd3",
                    color: "#b91c1c",
                    padding: "10px 12px",
                    borderRadius: 12,
                    fontWeight: 800,
                  }}
                >
                  {ringiError}
                </div>
              )}

              {!ringiLoading && !ringiError && (
                <>
                  <div
                    style={{
                      margin: 12,
                      borderRadius: 12,
                      border: "1px solid #bbf7d0",
                      background: "#ecfdf5",
                      overflow: "hidden",
                    }}
                  >
                    <div
  style={{
    padding: "10px 12px",
    fontWeight: 900,
    color: "#0f172a",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  }}
>
  <div>稟議内容</div>

  <div style={{ display: "flex", gap: 10 }}>
    <a
      href={`${BASE_URL}/staff/ringi/${merchantId}/preview`}
      target="_blank"
      rel="noreferrer"
      style={{
        padding: "6px 12px",
        borderRadius: 8,
        border: "1px solid #2563eb",
        color: "#2563eb",
        fontWeight: 900,
        textDecoration: "none",
        background: "#fff",
      }}
    >
      プレビュー
    </a>

    <a
      href={`${BASE_URL}/staff/ringi/${merchantId}/pdf`}
      target="_blank"
      rel="noreferrer"
      style={{
        padding: "6px 12px",
        borderRadius: 8,
        background: "#16a34a",
        color: "#fff",
        fontWeight: 900,
        textDecoration: "none",
      }}
    >
      PDFダウンロード
    </a>
  </div>
</div>

                   <ExcelRingiTable ringi={ringi} />
 
                  </div>

                  <div style={{ padding: "0 12px 12px 12px", color: "#64748b", fontWeight: 800, fontSize: 12 }}>
                    ※ この画面は承認済みのため、編集はできません。
                  </div>
                </>
              )}
            </div>
          )}
        </>
      )}
    </StaffLayout>
  );
}
